import { useEffect, useMemo, useRef, useState } from "react";
import { API_URL } from "../../../shared/utils/Util";
import { getFetchT } from "../../../shared/utils/TypedUtil";
import { ListCampaignsResponse } from "../../../types/api";

export interface CampaignOrderGroup {
  id: string;
  // Undefined until the campaign list has loaded (or if it failed to load).
  name?: string;
  pending: any[];
  done: any[];
}

export interface CampaignOrderGroups {
  // Orders that are not part of any campaign.
  regularPending: any[];
  regularDone: any[];
  // One group per campaign with at least one pending or done order, sorted
  // by name.
  campaigns: CampaignOrderGroup[];
}

/**
 * Splits campaign (group menu) orders out of the regular pending and done
 * orders so the control station can show them under per-campaign tabs.
 *
 * Campaign names are fetched lazily: the campaign list is only requested
 * when an order references a campaign we don't know yet, so a campaign
 * created after the page loaded is still labelled.
 */
export const useCampaignOrderGroups = (
  restaurantId: string,
  pendingOrders: any[],
  doneOrders: any[]
): CampaignOrderGroups => {
  const [campaignNames, setCampaignNames] = useState<Map<string, string>>(new Map());
  // Campaign IDs we've already fetched the campaign list for, so an unknown
  // (or failed) ID doesn't trigger a fetch on every render.
  const requestedIds = useRef<Set<string>>(new Set());

  const grouped = useMemo(() => {
    const regularPending: any[] = [];
    const regularDone: any[] = [];
    const byCampaign = new Map<string, { pending: any[]; done: any[] }>();
    const getGroup = (id: string) => {
      if (!byCampaign.has(id)) {
        byCampaign.set(id, { pending: [], done: [] });
      }
      return byCampaign.get(id)!;
    };

    pendingOrders.forEach((order) => {
      if (order.group_menu_id) {
        getGroup(order.group_menu_id).pending.push(order);
      } else {
        regularPending.push(order);
      }
    });
    doneOrders.forEach((order) => {
      if (order.group_menu_id) {
        getGroup(order.group_menu_id).done.push(order);
      } else {
        regularDone.push(order);
      }
    });

    return { regularPending, regularDone, byCampaign };
  }, [pendingOrders, doneOrders]);

  const unknownIds = Array.from(grouped.byCampaign.keys())
    .filter((id) => !campaignNames.has(id) && !requestedIds.current.has(id))
    .sort()
    .join(",");

  useEffect(() => {
    if (!unknownIds) {
      return;
    }
    unknownIds.split(",").forEach((id) => requestedIds.current.add(id));
    getFetchT<ListCampaignsResponse>(
      `${API_URL}/v1/r/${restaurantId}/campaigns`,
      (res) => {
        setCampaignNames(new Map(res.campaigns.map((c) => [c.id, c.name])));
      },
      (err) => {
        console.error("Failed to load campaigns", err);
      }
    );
  }, [restaurantId, unknownIds]);

  const campaigns = useMemo(
    () =>
      Array.from(grouped.byCampaign.entries())
        .map(([id, { pending, done }]) => ({
          id,
          name: campaignNames.get(id),
          pending,
          done,
        }))
        .sort((a, b) => (a.name ?? a.id).localeCompare(b.name ?? b.id)),
    [grouped, campaignNames]
  );

  return {
    regularPending: grouped.regularPending,
    regularDone: grouped.regularDone,
    campaigns,
  };
};
